import axios from "axios";
import { useAuthValue } from "context/AuthContext";
import useCartItems from "hooks/useCartItems";
import useOffCanvasReducer from "hooks/useOffCanvasReducer";
import useOrder from "hooks/useOrder";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import { selectUploads } from "store/selectors";
import { createUUID, getUrlParam } from "utils/helpers";


export default function usePersonalBrand() {

    const { productId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuthValue();
    const [products, setProducts] = useState();
    const [showOffCanvasServiceInfo, setShowOffCanvasServiceInfo] = useOffCanvasReducer();
    const [completedOrderData, setCompletedOrderData] = useState();
    const [qty, setQty] = useState(1);
    const [step, setStep] = useState(1);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState();
    const [addressValue, setAddressValue] = useState('');
    const [newAddress, setNewAddress] = useState(null);
    const [checkoutData, setCheckoutData] = useState({ note: '', agent_on_site: false });
    const [reservationConfirmData, setReservationConfirmData] = useState();
    const [organizationSelected, setOrganizationSelected] = useState();
    const [photographers, setPhotographers] = useState();
    const organizationId = user?.agent?.getSelectedOrganization()?.id || getUrlParam('org_id');

    const {
        placeOrder,
        isInProgress,
        isCartInProgress,
        couponData,
        applyCoupon
    } = useOrder();


    const productData = useMemo(() => {
        if (!products || !products.length) return null;
        return products[0];
    }, [products]);

    const priceAndTime = useMemo(() => {
        if (!productData) return { price: 0, time: 0 };
        const [ pricing ] = productData.price_matrix || {};
        return {
            time: (pricing.time || 0) * qty,
            price: (pricing[productData.custom_price_field] || 0) ,
            qty
        }
    }, [productData, qty]);

    useEffect(() => {
        axios.get(`/products/${productId}`, {
            params: {
                detailed: 1,
                organization_id: organizationId,
            }
        })
        .then(async res => {
            setProducts([{
                ...res.data,
                is_selected: true,
                qty: 1,
            }]);
            if (res.data.service_address) {
                setNewAddress(res.data.service_address);
                setAddressValue(res.data.service_address.address.full);
            }
            const userOrganization = user?.agent?.getSelectedOrganization();
            if (userOrganization) {
                setOrganizationSelected(userOrganization);
            } else {
                axios.get(`organizations/${organizationId}`)
                .then(res => {
                    setOrganizationSelected(res.data);
                });
            }

            if (user.is_admin) {
                const photographersRes = await axios.get('users', {
                    params: {
                        role: 'photographer'
                    }
                })
                setPhotographers(photographersRes.data);
            } else {
                setPhotographers([]);
            }
        });
    }, [productId]);

    const cartItems = useCartItems((products ?? []).map(p => ({
        ...p,
        ...priceAndTime
    })));

    const handleRetriveAddress = (autocompleteObj) => {
        setNewAddress({
            address: autocompleteObj.values,
            coords: autocompleteObj.coords
        });
    };
    
    const handleResetAddress = () => {
        setAddressValue('');
        setNewAddress(null);
    }

    const handleChangeQty = (val) => {
        setQty(val);
        setProducts(products.map(p => {
            if (!p.is_selected) return p;
            p.qty = val;
            return p;
        }));
    }

    const handleCloseModalConfirm = () => {
        navigate(`/orders?type=PLAN&action=showo&order_id=${completedOrderData.id}`);
    }

    const handleBack = () => {
        if (step === 1) {
            navigate('/');
        } else {
            setStep(step - 1);
        }
    }

    const handleChangeCheckoutData = (event) => {
        const { type, name, value, checked } = event.target;
        setCheckoutData({
            ...checkoutData,
            [name]: type === 'checkbox' ? checked : value
        });
    }

    const completeCheckout = (is_bacs=false) => {
        placeOrder({
            with_appointments: true,
            products: cartItems.products.map(p => ({
                id: p.id,
                is_priority: !!p.is_priority,
                qty: p.qty
            })),
            photographer_uuid: selectedTimeSlot.photographer_id,
            start_at: selectedTimeSlot.start.toISOString(),
            travel_paid: (selectedTimeSlot?.travel_paid?.km_refund ?? 0) + (selectedTimeSlot?.travel_paid?.toll ?? 0),
            is_bacs: is_bacs,
            organization_id: organizationId,
            is_generic: 1,
            ...newAddress,
            ...checkoutData
        })
        .then(data => {
            setReservationConfirmData(data);
        })
        .catch((error) => {
            error && toast.error(error);
        });
    }

    const handleCloseConfirm = () => {
        const appointment_id = reservationConfirmData.appointment_id;
        navigate(`${user.is_admin ? '/admin' : ''}/calendar/?action=show&id=${appointment_id}&date=${moment(reservationConfirmData.estimated_start_at).format('YYYY-MM-DD')}`);
        setReservationConfirmData();
    }

    const buildingData = useMemo(() => ({
        coords: newAddress?.coords,
        address: newAddress?.address,
        organization: organizationSelected,
    }), [newAddress, user, organizationSelected]);


    return {
        products,
        showOffCanvasServiceInfo, setShowOffCanvasServiceInfo,
        cartItems,
        checkoutData, handleChangeCheckoutData,
        qty, handleChangeQty,
        placeOrder,
        isInProgress,
        isCartInProgress,
        completedOrderData,
        handleCloseModalConfirm,
        couponData,
        applyCoupon,
        productData,
        step, setStep,
        addressValue, setAddressValue,
        handleRetriveAddress,
        handleResetAddress,
        newAddress,
        selectedTimeSlot, setSelectedTimeSlot,
        handleBack,
        buildingData,
        completeCheckout,
        reservationConfirmData, setReservationConfirmData,
        handleCloseConfirm,
        photographers,
        user
    }
}